import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface SmsBundleProduct {
  id: string;
  slug: string;
  name_da: string;
  name_en: string;
  segments: number;
  price_cents: number;
  is_active: boolean;
  sort_order: number;
}

export interface SmsBundleOrder {
  id: string;
  clinic_id: string;
  bundle_id: string | null;
  segments: number;
  price_cents: number;
  status: 'pending' | 'paid' | 'cancelled';
  requested_by: string | null;
  fulfilled_by: string | null;
  fulfilled_at: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformSmsClinicRow {
  clinic_id: string;
  clinic_name: string;
  parent_clinic_id: string | null;
  parent_clinic_name: string | null;
  sms_enabled: boolean;
  module_enabled: boolean;
  balance_cents: number;
  lifetime_purchased_cents: number;
  lifetime_used_cents: number;
  lifetime_purchased_segments: number;
  lifetime_used_segments: number;
  lifetime_used_messages: number;
  paid_orders: number;
  pending_orders: number;
}

export interface PlatformSmsOverview {
  price_per_segment_cents: number;
  clinics: PlatformSmsClinicRow[];
}

function asArray<T>(raw: unknown): T[] {
  return Array.isArray(raw) ? (raw as T[]) : [];
}

export const smsSegmentPriceQueryKey = ['sms', 'segment-price'] as const;
export const smsBundlesQueryKey = ['sms', 'bundles'] as const;
export const smsOrdersQueryKey = (clinicId: string | null | undefined) =>
  ['sms', 'orders', clinicId ?? 'all'] as const;
export const platformSmsOverviewQueryKey = ['sms', 'platform-overview'] as const;

export function useSmsSegmentPrice() {
  return useQuery({
    queryKey: smsSegmentPriceQueryKey,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc('get_sms_segment_price');
      if (error) throw error;
      const cents = Number((data as { price_per_segment_cents?: unknown } | null)?.price_per_segment_cents);
      return Number.isFinite(cents) && cents > 0 ? cents : 25;
    },
    staleTime: 30_000,
  });
}

export function useSmsBundleProducts() {
  return useQuery({
    queryKey: smsBundlesQueryKey,
    queryFn: async (): Promise<SmsBundleProduct[]> => {
      const { data, error } = await supabase.rpc('list_sms_bundle_products');
      if (error) throw error;
      return asArray<SmsBundleProduct>(data);
    },
    staleTime: 15_000,
  });
}

export function useSmsBundleOrders(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: smsOrdersQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<SmsBundleOrder[]> => {
      const { data, error } = await supabase.rpc('list_sms_bundle_orders', {
        p_clinic_id: clinicId ?? undefined,
      });
      if (error) throw error;
      return asArray<SmsBundleOrder>(data);
    },
    staleTime: 10_000,
  });
}

export function useRequestSmsBundleOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinic_id: string; bundle_id: string }) => {
      const { data, error } = await supabase.rpc('request_sms_bundle_order', {
        p_bundle_id: input.bundle_id,
        p_clinic_id: input.clinic_id,
      });
      if (error) throw error;
      return data as unknown as SmsBundleOrder;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: smsOrdersQueryKey(vars.clinic_id) });
      qc.invalidateQueries({ queryKey: platformSmsOverviewQueryKey });
    },
  });
}

export function useFulfillSmsBundleOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { order_id: string; note?: string }) => {
      const { data, error } = await supabase.rpc('fulfill_sms_bundle_order', {
        p_order_id: input.order_id,
        p_note: input.note ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms'] });
    },
  });
}

export function useCancelSmsBundleOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { order_id: string; note?: string }) => {
      const { data, error } = await supabase.rpc('cancel_sms_bundle_order', {
        p_order_id: input.order_id,
        p_note: input.note ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms'] });
    },
  });
}

export function usePlatformSmsOverview() {
  return useQuery({
    queryKey: platformSmsOverviewQueryKey,
    queryFn: async (): Promise<PlatformSmsOverview> => {
      const { data, error } = await supabase.rpc('get_platform_sms_overview');
      if (error) throw error;
      const raw = (data ?? {}) as Partial<PlatformSmsOverview>;
      return {
        price_per_segment_cents: Number(raw.price_per_segment_cents ?? 25),
        clinics: asArray<PlatformSmsClinicRow>(raw.clinics),
      };
    },
    staleTime: 10_000,
  });
}

export function useSetSmsSegmentPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cents: number) => {
      const { data, error } = await supabase.rpc('set_sms_segment_price', {
        p_price_per_segment_cents: cents,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smsSegmentPriceQueryKey });
      qc.invalidateQueries({ queryKey: platformSmsOverviewQueryKey });
    },
  });
}

export function useUpsertSmsBundleProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string | null;
      slug: string;
      name_da: string;
      name_en: string;
      segments: number;
      price_cents: number;
      is_active: boolean;
      sort_order: number;
    }) => {
      const { data, error } = await supabase.rpc('upsert_sms_bundle_product', {
        p_id: input.id ?? null,
        p_slug: input.slug,
        p_name_da: input.name_da,
        p_name_en: input.name_en,
        p_segments: input.segments,
        p_price_cents: input.price_cents,
        p_is_active: input.is_active,
        p_sort_order: input.sort_order,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: smsBundlesQueryKey });
    },
  });
}

export function useAdjustSmsCreditAsAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinic_id: string; delta_cents: number; reason: string }) => {
      const { data, error } = await supabase.rpc('adjust_sms_credit_as_admin', {
        p_clinic_id: input.clinic_id,
        p_delta_cents: input.delta_cents,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms'] });
    },
  });
}
